import { useWizard } from 'react-use-wizard';
import { useGAFormTracking } from '../../../../shared/hooks';
import { useReferralContext } from '../../context';
import Textarea from '@ramsey-design-system/textarea';
import {
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../stepComponents';
import { FieldErrors, useForm } from 'react-hook-form';
import { trackStepCompleted } from '../../../../shared/analytics';
import {
  getFormName,
  useTrackStepError,
  useTrackStepStarted
} from '../../utils';
import { StandardNextButton } from '../buttons';
import { yupResolver } from '@hookform/resolvers/yup';
import { generateSensitiveDataSchema } from '../../../../shared/utils';
import * as Yup from 'yup';

const STEP_NAME = 'ph_something_else_step';
const STEP_LABEL = 'Tell us more about your situation.';

interface PHSomethingElseStepFields {
  situation: string;
}

function PHSomethingElseStep() {
  const { nextStep, activeStep } = useWizard();
  const formRef = useGAFormTracking('ph_personal_intent_step');
  const { referral, updateReferral } = useReferralContext();

  const schema = generateSensitiveDataSchema(
    'situation'
  ) as unknown as Yup.ObjectSchema<PHSomethingElseStepFields>;
  const { register, handleSubmit, formState } =
    useForm<PHSomethingElseStepFields>({
      defaultValues: {
        situation: ''
      },
      resolver: yupResolver(schema)
    });

  const errorMessage = formState.errors.situation?.message;

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(values: { situation: string }) {
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['Tell us more about your situation'],
      answer_values: [values.situation],
      form_name: getFormName()
    });

    updateReferral({
      ...referral,
      ph_something_else: values.situation
    });
    await nextStep();
  }

  function onInvalid(formErrors: FieldErrors<PHSomethingElseStepFields>) {
    trackError('validation', formErrors.situation?.message, 'situation');
  }

  return (
    <StepWrapper>
      <form ref={formRef} onSubmit={handleSubmit(onValidSubmit, onInvalid)}>
        <StandardStepTitle>
          Tell us more about your situation.
        </StandardStepTitle>
        <StandardStepSubtitleText>Optional</StandardStepSubtitleText>
        <div>
          <Textarea
            {...register('situation')}
            maxLength={250}
            id="ph_something_else_textbox"
            aria-describedby="ph_something_else_textbox"
            data-testid="ph_something_else_textbox"
            value={referral?.ph_something_else}
            onChange={(event) =>
              updateReferral({
                ...referral,
                ph_something_else: event.target.value
              })
            }
            defaultValue={referral?.ph_something_else}
          />
        </div>
        {(errorMessage && (
          <p className="text-2 text-red-60 pt-1 pb-4">{errorMessage}</p>
        )) || <p className="pb-4"></p>}
        <StandardNextButton />
      </form>
    </StepWrapper>
  );
}

export { PHSomethingElseStep };
